import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { getstaffwithid } from "../../../api/FacultyService";
import { formatTime } from "../../master/Master";

const localizer = momentLocalizer(moment);

// ✅ Toolbar
const CustomToolbar = (toolbar) => {
  return (
    <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-4">
      <div className="d-none">
        <h4 className="fw-bold mb-1">Attendance Calendar</h4>

        <small className="text-muted">
          View daily attendance, leaves and holidays
        </small>
      </div>

      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
        <div className="btn-group">
          <button
            className="btn btn-outline-primary"
            onClick={() => toolbar.onNavigate("PREV")}
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>

          <button
            className="btn btn-primary"
            onClick={() => toolbar.onNavigate("TODAY")}
          >
            Today
          </button>

          <button
            className="btn btn-outline-primary"
            onClick={() => toolbar.onNavigate("NEXT")}
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>

        <h5 className="fw-bold mb-0">{toolbar.label}</h5>

        <div className="btn-group">
          <button
            className={`btn btn-outline-primary ${
              toolbar.view === "month" ? "active" : ""
            }`}
            onClick={() => toolbar.onView("month")}
          >
            Month
          </button>

          <button
            className={`btn btn-outline-primary ${
              toolbar.view === "week" ? "active" : ""
            }`}
            onClick={() => toolbar.onView("week")}
          >
            Week
          </button>

          <button
            className={`btn btn-outline-primary ${
              toolbar.view === "day" ? "active" : ""
            }`}
            onClick={() => toolbar.onView("day")}
          >
            Day
          </button>
        </div>
      </div>
    </div>
  );
};

// ✅ Multi-line Event
const CustomEvent = ({ event }) => {
  if (event.title === "Leave") return <strong>🌴 Leave</strong>;

  if (event.title === "Sunday") return <strong>☀ Sunday</strong>;

  if (event.title === "Absent") return <strong>❌ Absent</strong>;

  return (
    <div
      style={{
        fontSize: 11,
        lineHeight: 1.3,
      }}
    >
      {event.title.split("||").map((line, index) => (
        <div key={index}>{line.replace("|", " • ")}</div>
      ))}
    </div>
  );
};

const AttandanceTab = () => {
  const { id } = useParams();
  const [events, setEvents] = useState([]);

  useEffect(() => {
    const staffFatch = async () => {
      try {
        const response = await getstaffwithid(id, {
          type: "attendance",
        });

        if (response.data?.status) {
          const staff = response.data.data;
          let evts = [];

          const grouped = {};

          // ✅ Group attendance
          staff.attendances?.forEach((att) => {
            const date = att.date;
            if (!grouped[date]) grouped[date] = [];
            grouped[date].push(att);
          });

          // ✅ Main Logic
          Object.keys(grouped).forEach((date) => {
            const entries = grouped[date];

            let title = "";
            let status = "success";

            let hasLeave = entries.some((e) => Number(e.is_leave) === 1);
            let inCount = entries.filter((e) => !!e.check_in_time).length;
            let outCount = entries.filter((e) => !!e.check_out_time).length;

            if (
              hasLeave &&
              ((inCount === 0 && outCount === 0) ||
                (inCount >= 2 && outCount >= 2))
            ) {
              title = "Absent";
              status = "absent";
            } else {
              title = entries
                .map((e) => {
                  let inTime = e?.check_in_time
                    ? formatTime(e.check_in_time)
                    : "--";
                  let outTime = e?.check_out_time
                    ? formatTime(e.check_out_time)
                    : null;

                  if (!outTime) {
                    status = "warning";
                    return `${inTime}`;
                  }

                  return `${inTime} → ${outTime}`;
                })
                .join("||");
            }

            evts.push({
              title,
              start: new Date(date),
              end: new Date(date),
              allDay: true,
              status,
            });
          });

          // ✅ Leave
          staff.leave_request?.forEach((lv) => {
            if (lv.type === "staff") {
              evts.push({
                title: "Leave",
                start: new Date(lv.date),
                end: new Date(lv.date),
                allDay: true,
              });
            }
          });

          // ✅ Sunday
          let startYear = moment().startOf("year");
          let endYear = moment().endOf("year");

          for (
            let m = startYear.clone();
            m.isSameOrBefore(endYear);
            m.add(1, "days")
          ) {
            if (m.day() === 0) {
              evts.push({
                title: "Sunday",
                start: m.toDate(),
                end: m.toDate(),
                allDay: true,
              });
            }
          }

          setEvents(evts);
        }
      } catch (err) {
        console.error("API error", err);
      }
    };

    staffFatch();
  }, [id]);

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body p-4">
        <div className="row g-3 mb-4">
          {[
            {
              title: "Present",
              count: events.filter((e) => e.status === "success").length,
              icon: "ri-user-follow-line",
              color: "success",
              bg: "#ecfdf3",
            },
            {
              title: "Incomplete",
              count: events.filter((e) => e.status === "warning").length,
              icon: "ri-time-line",
              color: "warning",
              bg: "#fff8e6",
            },
            {
              title: "Absent",
              count: events.filter((e) => e.status === "absent").length,
              icon: "ri-user-unfollow-line",
              color: "danger",
              bg: "#fff1f2",
            },
            {
              title: "Leaves",
              count: events.filter((e) => e.title === "Leave").length,
              icon: "ri-calendar-check-line",
              color: "info",
              bg: "#eff6ff",
            },
          ].map((item) => (
            <div className="col-xl-3 col-md-6" key={item.title}>
              <div
                className="card border-0 shadow-sm h-100"
                style={{ borderRadius: 16 }}
              >
                <div className="card-body py-3 px-4">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <div className={`text-${item.color} fw-semibold`}>
                        {item.title}
                      </div>

                      <h2 className="fw-bold mt-2 mb-0">{item.count}</h2>
                    </div>

                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: 52,
                        height: 52,
                        background: item.bg,
                      }}
                    >
                      <i
                        className={item.icon}
                        style={{
                          fontSize: 24,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="d-flex gap-4 mb-3 flex-wrap">
          {[
            ["Present", "success"],
            ["Incomplete", "warning"],
            ["Absent", "danger"],
            ["Leave", "primary"],
            ["Sunday", "secondary"],
          ].map(([label, color]) => (
            <div key={label} className="d-flex align-items-center">
              <div
                className={`rounded-circle bg-${color}`}
                style={{
                  width: 12,
                  height: 12,
                }}
              ></div>

              <span className="ms-2">{label}</span>
            </div>
          ))}
        </div>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          selectable={false}
          style={{
            height: 600,
            background: "#fff",
            borderRadius: 16,
            padding: 10,
          }}
          views={["month", "week", "day"]}
          components={{
            toolbar: CustomToolbar,
            event: CustomEvent,
          }}
          eventPropGetter={(event) => {
            let background = "#16a34a";

            let color = "#fff";

            if (event.title === "Leave") {
              background = "#2563eb";
            } else if (event.title === "Sunday") {
              background = "#6b7280";
            } else if (event.status === "warning") {
              background = "#f59e0b";
            } else if (event.status === "absent") {
              background = "#ef4444";
            }

            return {
              style: {
                background,

                color,

                borderRadius: 8,

                border: "none",

                padding: "4px 8px",

                fontWeight: 600,

                fontSize: 11,
              },
            };
          }}
        />
      </div>
    </div>
  );
};

export default AttandanceTab;
